import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import {
  User,
  Heart,
  Pill,
  FileText,
  Activity,
  Shield,
  AlertTriangle,
  Sparkles,
  Download,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import GlassCard from '@/components/ui/glass-card';

interface SummarySection {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  content: React.ReactNode;
}

const AIHealthSummary: React.FC = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [lastGenerated, setLastGenerated] = useState(new Date());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['demographics', 'conditions', 'medications', 'tests', 'vitals', 'preventive', 'risk', 'recommendations'])
  );

  const toggleSection = (id: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setLastGenerated(new Date());
    setIsGenerating(false);
  };

  const handleExport = () => {
    alert('Export as PDF - Coming soon!');
  };

  const sections: SummarySection[] = [
    {
      id: 'demographics',
      title: 'Demographics',
      icon: User,
      content: (
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Age:</span>
            <span className="font-medium text-gray-900 dark:text-white">45 years</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Sex:</span>
            <span className="font-medium text-gray-900 dark:text-white">Male</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Blood Type:</span>
            <span className="font-medium text-gray-900 dark:text-white">O+</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Height:</span>
            <span className="font-medium text-gray-900 dark:text-white">5'10" (178 cm)</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Weight:</span>
            <span className="font-medium text-gray-900 dark:text-white">185 lbs (84 kg)</span>
          </div>
        </div>
      ),
    },
    {
      id: 'conditions',
      title: 'Active Conditions',
      icon: Heart,
      content: (
        <div className="space-y-3">
          <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
            <h4 className="font-semibold text-red-900 dark:text-red-300 mb-1">Type 2 Diabetes</h4>
            <p className="text-sm text-red-700 dark:text-red-400">Diagnosed: March 2019</p>
          </div>
          <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
            <h4 className="font-semibold text-orange-900 dark:text-orange-300 mb-1">Hypertension</h4>
            <p className="text-sm text-orange-700 dark:text-orange-400">Diagnosed: July 2020</p>
          </div>
          <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
            <h4 className="font-semibold text-yellow-900 dark:text-yellow-300 mb-1">Hyperlipidemia</h4>
            <p className="text-sm text-yellow-700 dark:text-yellow-400">Diagnosed: January 2021</p>
          </div>
        </div>
      ),
    },
    {
      id: 'medications',
      title: 'Current Medications',
      icon: Pill,
      content: (
        <div className="space-y-2">
          <div className="flex justify-between items-start">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Metformin</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">500mg twice daily</p>
            </div>
            <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded">Active</span>
          </div>
          <div className="flex justify-between items-start">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Lisinopril</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">20mg once daily</p>
            </div>
            <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded">Active</span>
          </div>
          <div className="flex justify-between items-start">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Atorvastatin</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">40mg once daily at bedtime</p>
            </div>
            <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded">Active</span>
          </div>
          <div className="flex justify-between items-start">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Vitamin D3</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">2000 IU once daily</p>
            </div>
            <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded">Active</span>
          </div>
        </div>
      ),
    },
    {
      id: 'tests',
      title: 'Recent Test Results',
      icon: FileText,
      content: (
        <div className="space-y-3">
          <div>
            <div className="flex justify-between mb-1">
              <span className="font-medium text-gray-900 dark:text-white">Hemoglobin A1C</span>
              <span className="text-yellow-600 dark:text-yellow-400 font-semibold">6.8%</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">2 weeks ago • Target: &lt;7.0%</p>
          </div>
          <div>
            <div className="flex justify-between mb-1">
              <span className="font-medium text-gray-900 dark:text-white">LDL Cholesterol</span>
              <span className="text-green-600 dark:text-green-400 font-semibold">118 mg/dL</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">5 weeks ago • Target: &lt;100 mg/dL</p>
          </div>
          <div>
            <div className="flex justify-between mb-1">
              <span className="font-medium text-gray-900 dark:text-white">Blood Pressure</span>
              <span className="text-yellow-600 dark:text-yellow-400 font-semibold">128/82 mmHg</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">1 day ago • Target: &lt;130/80 mmHg</p>
          </div>
        </div>
      ),
    },
    {
      id: 'vitals',
      title: 'Vital Signs Trends',
      icon: Activity,
      content: (
        <div className="prose prose-sm dark:prose-invert max-w-none">
          <p className="text-gray-700 dark:text-gray-300">
            Your vital signs show positive trends over the past 90 days. Blood pressure has decreased from an average of 135/88 to 128/82 mmHg, indicating good response to medication adjustments. Weight has decreased by 3 lbs, supporting improved metabolic control. Resting heart rate remains stable at 68 bpm, consistent with regular physical activity tracked by wearable devices.
          </p>
        </div>
      ),
    },
    {
      id: 'preventive',
      title: 'Preventive Care Status',
      icon: Shield,
      content: (
        <div className="space-y-2">
          <div className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-900/20 rounded">
            <span className="text-sm text-gray-900 dark:text-white">Annual Physical Exam</span>
            <span className="text-xs text-green-700 dark:text-green-400 font-medium">✓ Completed</span>
          </div>
          <div className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-900/20 rounded">
            <span className="text-sm text-gray-900 dark:text-white">Diabetic Eye Exam</span>
            <span className="text-xs text-green-700 dark:text-green-400 font-medium">✓ Completed</span>
          </div>
          <div className="flex items-center justify-between p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
            <span className="text-sm text-gray-900 dark:text-white">Flu Vaccine</span>
            <span className="text-xs text-yellow-700 dark:text-yellow-400 font-medium">⚠ Due Soon</span>
          </div>
          <div className="flex items-center justify-between p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
            <span className="text-sm text-gray-900 dark:text-white">Colonoscopy</span>
            <span className="text-xs text-yellow-700 dark:text-yellow-400 font-medium">⚠ Due in 3 months</span>
          </div>
        </div>
      ),
    },
    {
      id: 'risk',
      title: 'Risk Assessment',
      icon: AlertTriangle,
      content: (
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-gray-900 dark:text-white">Overall Risk Score</span>
              <span className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">Moderate</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '60%' }} />
            </div>
          </div>
          <div className="space-y-2">
            <h5 className="font-medium text-gray-900 dark:text-white text-sm">Key Risk Factors:</h5>
            <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-red-500 mt-0.5">•</span>
                <span>Elevated cardiovascular risk due to diabetes and hypertension</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-500 mt-0.5">•</span>
                <span>BMI of 27.2 indicates overweight status</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-500 mt-0.5">•</span>
                <span>A1C slightly above optimal target</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">•</span>
                <span>Good medication adherence and regular monitoring</span>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: 'recommendations',
      title: 'AI Recommendations',
      icon: Sparkles,
      content: (
        <div className="space-y-3">
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-l-4 border-blue-500">
            <h5 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">1. Optimize Blood Glucose Control</h5>
            <p className="text-sm text-blue-800 dark:text-blue-400">
              Your A1C of 6.8% is close to target but can be improved. Consider increasing daily step count to 12,000 (currently averaging 8,500) and review carbohydrate intake with your dietitian.
            </p>
          </div>
          <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border-l-4 border-purple-500">
            <h5 className="font-semibold text-purple-900 dark:text-purple-300 mb-2">2. Weight Management Focus</h5>
            <p className="text-sm text-purple-800 dark:text-purple-400">
              Recent 3 lb weight loss is encouraging. Target 7-10% total body weight reduction (13-18 lbs) over next 6 months to significantly improve diabetes and blood pressure control.
            </p>
          </div>
          <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border-l-4 border-green-500">
            <h5 className="font-semibold text-green-900 dark:text-green-300 mb-2">3. Medication Timing Review</h5>
            <p className="text-sm text-green-800 dark:text-green-400">
              Wearable data shows blood pressure tends to spike in early morning. Discuss with your physician about timing Lisinopril dose for evening instead of morning.
            </p>
          </div>
          <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border-l-4 border-orange-500">
            <h5 className="font-semibold text-orange-900 dark:text-orange-300 mb-2">4. Schedule Preventive Care</h5>
            <p className="text-sm text-orange-800 dark:text-orange-400">
              Flu vaccine is due. Schedule appointment before peak flu season. Colonoscopy screening is due in 3 months - book now for preferred timing.
            </p>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Action Bar */}
      <div className="mb-6 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
              'bg-blue-600 hover:bg-blue-700 text-white',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            <RefreshCw className={cn('w-4 h-4', isGenerating && 'animate-spin')} />
            <span>{isGenerating ? 'Generating...' : 'Generate Summary'}</span>
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export PDF</span>
          </button>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <Sparkles className="w-4 h-4" />
          <span>Last generated: {lastGenerated.toLocaleString()}</span>
        </div>
      </div>

      {/* Summary Sections */}
      <div className="space-y-4">
        {sections.map((section) => {
          const Icon = section.icon;
          const isExpanded = expandedSections.has(section.id);

          return (
            <GlassCard key={section.id} className="overflow-hidden">
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {section.title}
                  </h3>
                </div>
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                )}
              </button>

              {isExpanded && (
                <div className="px-4 pb-4 pt-0">
                  {section.content}
                </div>
              )}
            </GlassCard>
          );
        })}
      </div>
    </div>
  );
};

export default AIHealthSummary;
